import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../firebaseConfig";
import AccountingShell from "./AccountingShell";
import toast from "react-hot-toast";

const GST_RATE = 18;
const INR = (n) => `₹ ${Number(n || 0).toLocaleString("en-IN")}`;
const num = (v) => Number(v || 0);

export default function CEOApprovalDesk() {
  return (
    <AccountingShell title="CEO Approval Desk">
      <CEOApprovalContent />
    </AccountingShell>
  );
}

function CEOApprovalContent({ me }) {
  const [raBills, setRaBills] = useState([]);
  const [tenders, setTenders] = useState([]);

  const load = async () => {
    const [raSnap, tenderSnap] = await Promise.all([
      getDocs(collection(db, "ra_bills")),
      getDocs(collection(db, "tenders")),
    ]);

    setRaBills(raSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    setTenders(tenderSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
  };

  useEffect(() => {
    load();
  }, []);

  const rows = useMemo(() => {
    return raBills
      .filter((r) => r.accountingStatus === "ACCOUNTANT_VERIFIED")
      .map((r) => {
        const tender = tenders.find((t) => t.id === r.tenderId) || {};
        const basic = num(r.basicAmount || r.totalAmount || r.grossAmount);
        const gst = num(r.gstAmount || Math.round((basic * GST_RATE) / 100));
        const gross = num(r.grossWithGST || basic + gst);
        const net = num(r.netPayable || gross);

        const l1Amount = num(
          tender.awardedAmount ||
            tender.tenderAwardAmount ||
            tender.awardedValue ||
            r.l1Amount ||
            0
        );

        const alreadyApproved = raBills
          .filter(
            (x) =>
              x.tenderId === r.tenderId &&
              x.vendorUid === r.vendorUid &&
              x.accountingStatus === "CEO_APPROVED" &&
              x.id !== r.id
          )
          .reduce((s, x) => s + num(x.netPayable || x.grossWithGST), 0);

        return {
          ...r,
          tender,
          pacsName:
            tender.ownerCompany ||
            tender.pacsName ||
            tender.projectName ||
            tender.title ||
            "—",
          contractorName:
            r.vendorName ||
            r.contractorName ||
            tender.awardedVendorName ||
            r.vendorUid ||
            "—",
          basic,
          gst,
          gross,
          net,
          l1Amount,
          alreadyApproved,
          balanceAfterThis: alreadyApproved + net,
          availableBalance: l1Amount - alreadyApproved,
        };
      });
  }, [raBills, tenders]);

  const ceoApprove = async (r) => {
    if (r.l1Amount > 0 && r.balanceAfterThis > r.l1Amount) {
      toast.error(
        `Approve blocked. L1 ${INR(r.l1Amount)} hai, already approved ${INR(
          r.alreadyApproved
        )}, current bill ${INR(r.net)}. Limit cross ho rahi hai.`
      );
      return;
    }

    await updateDoc(doc(db, "ra_bills", r.id), {
      accountingStatus: "CEO_APPROVED",
      ceoApprovedBy: me.uid,
      ceoApprovedAt: serverTimestamp(),
      paymentReleaseMailStatus: "MAIL_TO_ACCOUNT_PENDING",
      l1Amount: r.l1Amount,
      alreadyApprovedAmount: r.alreadyApproved,
      balanceAfterCurrentBill: r.balanceAfterThis,
      availableBalanceBeforeBill: r.availableBalance,
    });

    await addDoc(collection(db, "contractor_ledgers"), {
      vendorUid: r.vendorUid || "",
      vendorName: r.contractorName,
      tenderId: r.tenderId,
      raBillId: r.id,
      billNo: r.billNo || r.raBillNo || "",
      type: "RA_BILL_PAYABLE",
      debit: r.net,
      credit: 0,
      gstAmount: r.gst,
      grossWithGST: r.gross,
      narration: "CEO approved RA bill for payment release",
      status: "PAYMENT_RELEASE_PENDING",
      createdAt: serverTimestamp(),
      createdBy: me.uid,
      createdByRole: me.role,
    });

    await addDoc(collection(db, "mail_queue"), {
      to: "account@shineagri.org",
      subject: "Payment Release Approval - RA Bill",
      type: "RA_BILL_PAYMENT_RELEASE",
      status: "PENDING",
      tenderId: r.tenderId,
      raBillId: r.id,
      contractorName: r.contractorName,
      amount: r.net,
      message: `CEO approval received. Please release payment of ${INR(
        r.net
      )} to ${r.contractorName}.`,
      createdAt: serverTimestamp(),
    });

    toast.success("CEO approved + ledger posted + mail queued");
    load();
  };

  const rejectToAccountant = async (r) => {
    const reason = window.prompt("Reject / correction reason:");
    if (!reason) return;

    await updateDoc(doc(db, "ra_bills", r.id), {
      accountingStatus: "CEO_REJECTED_CORRECTION",
      ceoRejectReason: reason,
      ceoRejectedBy: me.uid,
      ceoRejectedAt: serverTimestamp(),
    });

    toast.success("Returned for correction");
    load();
  };

  if (me.role !== "CEO") {
    return (
      <div className="bg-white p-8 rounded-2xl border">
        Only CEO can access this page.
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border p-5">
      <h2 className="font-black mb-4">RA Bills Pending for CEO Approval</h2>

      <div className="overflow-auto">
        <table className="min-w-[1500px] w-full text-xs border">
          <thead className="bg-slate-900 text-white">
            <tr>
              <th className="p-3 border text-left">PACS / Project</th>
              <th className="p-3 border text-left">Contractor</th>
              <th className="p-3 border text-left">Bill No</th>
              <th className="p-3 border text-right">Basic</th>
              <th className="p-3 border text-right">GST</th>
              <th className="p-3 border text-right">Net Payable</th>
              <th className="p-3 border text-right">L1 Amount</th>
              <th className="p-3 border text-right">Already Approved</th>
              <th className="p-3 border text-right">After This Bill</th>
              <th className="p-3 border text-left">Limit Check</th>
              <th className="p-3 border text-center">GST Invoice</th>
              <th className="p-3 border text-center">Action</th>
            </tr>
          </thead>

          <tbody>
            {rows.map((r) => {
              const overLimit = r.l1Amount > 0 && r.balanceAfterThis > r.l1Amount;

              return (
                <tr key={r.id} className={overLimit ? "bg-red-50" : ""}>
                  <td className="p-3 border font-black">{r.pacsName}</td>
                  <td className="p-3 border">{r.contractorName}</td>
                  <td className="p-3 border font-black">
                    {r.billNo || r.raBillNo || r.id}
                  </td>
                  <td className="p-3 border text-right">{INR(r.basic)}</td>
                  <td className="p-3 border text-right text-blue-700">
                    {INR(r.gst)}
                  </td>
                  <td className="p-3 border text-right text-emerald-700 font-black">
                    {INR(r.net)}
                  </td>
                  <td className="p-3 border text-right font-black">
                    {INR(r.l1Amount)}
                  </td>
                  <td className="p-3 border text-right">
                    {INR(r.alreadyApproved)}
                  </td>
                  <td className="p-3 border text-right font-black">
                    {INR(r.balanceAfterThis)}
                  </td>
                  <td className="p-3 border">
                    {overLimit ? (
                      <span className="text-red-600 font-black">
                        BLOCKED - L1 LIMIT CROSS
                      </span>
                    ) : (
                      <span className="text-emerald-700 font-black">
                        OK - WITHIN LIMIT
                      </span>
                    )}
                  </td>
                  <td className="p-3 border text-center">
                    {r.gstInvoiceUrl ? (
                      <button
                        className="btn-gray"
                        onClick={() => window.open(r.gstInvoiceUrl, "_blank")}
                      >
                        View
                      </button>
                    ) : (
                      <span className="text-red-600 font-black">Missing</span>
                    )}
                  </td>
                  <td className="p-3 border">
                    <div className="flex gap-2 justify-center">
                      <button className="btn-red" onClick={() => rejectToAccountant(r)}>
                        Return
                      </button>
                      <button
                        className={overLimit ? "btn-disabled" : "btn"}
                        disabled={overLimit}
                        onClick={() => ceoApprove(r)}
                      >
                        CEO Approve
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}

            {!rows.length && (
              <tr>
                <td colSpan={12} className="p-8 text-center text-slate-500">
                  No RA bills pending for CEO approval.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <style>{`
        .btn{background:#059669;color:white;padding:7px 12px;border-radius:10px;font-weight:800;font-size:11px}
        .btn-red{background:#dc2626;color:white;padding:7px 12px;border-radius:10px;font-weight:800;font-size:11px}
        .btn-gray{background:#e2e8f0;color:#0f172a;padding:7px 12px;border-radius:10px;font-weight:800;font-size:11px}
        .btn-disabled{background:#94a3b8;color:white;padding:7px 12px;border-radius:10px;font-weight:800;font-size:11px;cursor:not-allowed}
      `}</style>
    </div>
  );
}